import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/ui/lib/utils';
import { Input } from './input';

// shadcn の InputGroup に倣い、枠線・背景・状態の表現は入力欄でなくグループが持つ。
// アイコンなどのアドオンを入力欄と同じ枠の中に flex で並べるため。
// 状態の定義は inputVariants（input.tsx）の写し。片方を変えたらもう片方も直すこと。
// 子の form control は 1 つだけの前提で has-* を要素で絞っていない
// （ボタン系アドオンを加える時は [data-slot=input-group-control] で絞る）。
// フォーカスは focus-visible で判定する（テキスト入力ではマウス操作でも付くため見え方は focus と同じ）。
const inputGroupVariants = cva(
  cn(
    'tw:box-border tw:flex tw:w-full tw:min-w-0 tw:items-center tw:rounded-md tw:bg-background tw:text-foreground tw:transition-colors',
    // アドオンの色は variant 側で [&_[data-slot=input-group-addon]] により個別に指定されるため、
    // disabled の muted 化はここで明示的に上書きしないと勝てない
    'tw:has-disabled:pointer-events-none tw:has-disabled:border-muted tw:has-disabled:bg-muted tw:has-disabled:text-muted-foreground tw:has-disabled:[&_[data-slot=input-group-addon]]:text-muted-foreground',
  ),
  {
    variants: {
      // default: Input の default と同じ状態系（未入力 / 入力済み / フォーカス / エラー / disabled）
      // - 未入力・エラーの強調には not-has-disabled を付け、disabled の見た目が
      //   has-disabled 系だけで決まるようにする
      // - エラー + 未入力は 4 条件の指定で未入力の強調に勝たせる（3 条件同士では詳細度が同じ）
      // - アイコン色は input-icon（FDN UI ガイドラインの input（Icon=on））
      variant: {
        default: cn(
          'tw:h-12 tw:border-2 tw:border-input-filled-border',
          'tw:has-placeholder-shown:not-has-focus-visible:not-has-disabled:border-input-empty-border tw:has-placeholder-shown:not-has-focus-visible:not-has-disabled:bg-input-empty-background',
          'tw:has-focus-visible:border-ring tw:has-focus-visible:bg-background',
          'tw:has-aria-invalid:not-has-focus-visible:not-has-disabled:border-transparent tw:has-aria-invalid:not-has-focus-visible:not-has-disabled:bg-input-error-background',
          'tw:has-aria-invalid:has-placeholder-shown:not-has-focus-visible:not-has-disabled:border-transparent tw:has-aria-invalid:has-placeholder-shown:not-has-focus-visible:not-has-disabled:bg-input-error-background',
          'tw:[&_[data-slot=input-group-addon]]:text-input-icon',
        ),
        // plain: Input の plain と同じ。1px 枠線・高さ 40px・未入力の強調なし。
        // - エラーは destructive 枠線
        // - フォーカスは現行 Bootstrap に合わせ primary 枠線 + primary/25 リング
        // - disabled は現行 Bootstrap どおり背景だけ変えて枠線を残す
        // - アイコンは NativeSelect の plain と同じく foreground
        plain: cn(
          'tw:h-10 tw:border tw:border-form-border',
          'tw:has-focus-visible:border-primary tw:has-focus-visible:ring-3 tw:has-focus-visible:ring-primary/25',
          'tw:has-aria-invalid:not-has-focus-visible:not-has-disabled:border-destructive',
          'tw:has-disabled:border-form-border',
          'tw:[&_[data-slot=input-group-addon]]:text-foreground',
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type InputGroupProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof inputGroupVariants>;

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="input-group"
      className={cn(inputGroupVariants({ variant }), className)}
      {...props}
    />
  ),
);

InputGroup.displayName = 'InputGroup';

// 枠線・背景・文字色はグループが描くため、Input を包んで見た目を打ち消す（shadcn と同じ方式）。
// 打ち消しは CSS の詳細度ではなく cn()（tailwind-merge）で行う。同じ variant 接頭辞の
// クラスは後勝ちで置き換わるため、Input の状態クラスと接頭辞を揃えたリセットを渡す。
// inputVariants（input.tsx）の default の接頭辞を変えたら、このリストも直すこと。
// 枠線色・リング色のクラスは残るが、幅が 0 なので描画されない。
const inputGroupControlReset = cn(
  'tw:h-full tw:flex-1 tw:rounded-none tw:border-0 tw:bg-transparent tw:text-inherit',
  'tw:disabled:bg-transparent tw:disabled:text-inherit',
  'tw:not-focus:not-disabled:placeholder-shown:bg-transparent',
  'tw:focus:bg-transparent',
  'tw:aria-invalid:not-focus:not-disabled:bg-transparent tw:aria-invalid:not-focus:not-disabled:placeholder-shown:bg-transparent',
);

// variant はグループ側が持つので受け付けない（Input は常に default を描き、上のリセットで消す）
type InputGroupInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  'className' | 'variant'
> & {
  className?: string;
};

const InputGroupInput = React.forwardRef<HTMLElement, InputGroupInputProps>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      data-slot="input-group-control"
      className={cn(inputGroupControlReset, className)}
      {...props}
    />
  ),
);

InputGroupInput.displayName = 'InputGroupInput';

// 末尾のアドオン。ガイドラインどおり右端から 10px・24px のアイコンを想定する。
// 色はグループの variant が決める。装飾をクリックしても入力欄にフォーカスが移るよう、
// shadcn と同じくクリックを入力欄へ転送する。
const InputGroupAddon = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, onClick, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="input-group-addon"
    className={cn(
      'tw:flex tw:cursor-text tw:items-center tw:px-2.5 tw:*:size-6',
      className,
    )}
    onClick={e => {
      onClick?.(e);
      e.currentTarget.parentElement
        ?.querySelector<HTMLElement>('[data-slot=input-group-control]')
        ?.focus();
    }}
    {...props}
  />
));

InputGroupAddon.displayName = 'InputGroupAddon';

export { InputGroup, InputGroupAddon, InputGroupInput, inputGroupVariants };
